import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  DollarSign, TrendingUp, Users, MapPin, Truck, Package,
  Loader2, RefreshCw, ArrowRight, Target, Clock, ShieldCheck,
  AlertTriangle, BarChart3, Scale, Navigation
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Area, AreaChart
} from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#10b981','#3b82f6','#f59e0b','#ef4444','#8b5cf6','#ec4899','#06b6d4','#84cc16','#f97316','#6366f1'];

// Compact format for chart axes / tooltips space-constrained zones
const fmt = (v) => {
  if (v === null || v === undefined) return '0';
  if (v >= 1e9) return `${(v/1e9).toFixed(1)}Md`;
  if (v >= 1e6) return `${(v/1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v/1e3).toFixed(0)}K`;
  return v.toLocaleString('fr-FR');
};

// Exact amount formatter — used on KPI cards & tooltips so users see real figures (no rounding)
const fmtExact = (v) => {
  if (v === null || v === undefined || isNaN(v)) return '0';
  return Math.round(v).toLocaleString('fr-FR');
};

const fxof = (v) => `${fmt(v)} FCFA`;
const fxofExact = (v) => `${fmtExact(v)} FCFA`;

const PERIODS = [
  { key: '1m', label: '1 mois' },
  { key: '3m', label: '3 mois' },
  { key: '6m', label: '6 mois' },
  { key: '1y', label: '1 an' },
  { key: '2y', label: '2 ans' },
  { key: 'all', label: 'Tout' },
];

const TABS = [
  { key: 'overview', label: 'Tableau de Bord', icon: BarChart3 },
  { key: 'carriers', label: 'Transporteurs', icon: Truck },
  { key: 'routes', label: 'Trajets', icon: MapPin },
  { key: 'profitability', label: 'Rentabilité', icon: Target },
  { key: 'costTonne', label: 'Coût/Tonne', icon: Scale },
  { key: 'costVehicle', label: 'Coût/Véhicule', icon: Truck },
  { key: 'costDest', label: 'Coût/Destination', icon: Navigation },
];

const KPICard = ({ label, value, sub, icon: Icon, color = 'emerald', alert }) => (
  <Card data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}
    className={`border ${alert ? 'border-red-200 bg-red-50/30' : 'border-gray-100'}`}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
          <p className={`text-lg font-bold mt-1 text-${color}-600 break-words leading-tight`} style={{ fontFamily: 'monospace' }} title={value}>{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
        <div className={`p-2 rounded-lg bg-${color}-50 shrink-0 ml-2`}>
          <Icon className={`h-4 w-4 text-${color}-600`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg text-xs">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="flex justify-between gap-4">
          <span>{p.name}</span>
          <span className="font-mono font-semibold">{fxofExact(p.value)}</span>
        </p>
      ))}
    </div>
  );
};

// ─── TAB: Overview ───
const OverviewTab = ({ data }) => {
  const { kpis, evolution, concentration, paymentMethods } = data;
  // Support 3 KPIs distincts (PM Spec 12/02/2026) : Spot / Contrats / Consolidé
  const hasContractData = typeof kpis.revenueConsolidated !== 'undefined';
  const spotRev = kpis.revenueSpot ?? kpis.revenue ?? 0;
  const contractRev = kpis.revenueContract ?? 0;
  const consolidatedRev = kpis.revenueConsolidated ?? spotRev;
  const contractInvoicesCount = kpis.contractInvoicesCount ?? 0;

  // Marge Brute — 3 KPIs distincts (PM 24/02/2026)
  const marginSpot = kpis.grossMarginSpot ?? kpis.grossMargin ?? 0;
  const marginSpotPct = kpis.grossMarginSpotPercent ?? kpis.grossMarginPercent ?? 0;
  const marginContract = kpis.grossMarginContract ?? 0;
  const marginContractPct = kpis.grossMarginContractPercent ?? 0;
  const marginConsolidated = kpis.grossMarginConsolidated ?? marginSpot;
  const marginConsolidatedPct = kpis.grossMarginConsolidatedPercent ?? marginSpotPct;
  const marginColor = (pct) => (pct >= 20 ? 'emerald' : pct >= 10 ? 'orange' : 'red');

  return (
    <div className="space-y-5">
      {/* Ligne 1 : 3 KPIs CA distincts (Spot / Contrats / Consolidé) */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="kpi-row-ca">
        <KPICard label="CA Spot" value={fxofExact(spotRev)} sub={`${data.totalOperations} opérations`} icon={DollarSign} color="emerald" />
        {hasContractData && (
          <KPICard label="CA Contrats" value={fxofExact(contractRev)} sub={`${contractInvoicesCount} factures récurrentes`} icon={DollarSign} color="blue" />
        )}
        {hasContractData && (
          <KPICard label="CA Consolidé" value={fxofExact(consolidatedRev)} sub="Spot + Contrats" icon={TrendingUp} color="violet" />
        )}
      </div>
      {/* Ligne 2 : 3 KPIs Marge Brute (Spot / Contrats / Consolidé) — PM 24/02/2026 */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="kpi-row-marge">
        <KPICard
          label="Marge Brute Spot"
          value={fxofExact(marginSpot)}
          sub={`${marginSpotPct}% du CA spot`}
          icon={TrendingUp}
          color={marginColor(marginSpotPct)}
        />
        {hasContractData && (
          <KPICard
            label="Marge Brute Contrats"
            value={fxofExact(marginContract)}
            sub={`${marginContractPct}% du CA contrats`}
            icon={TrendingUp}
            color={marginColor(marginContractPct)}
          />
        )}
        {hasContractData && (
          <KPICard
            label="Marge Brute Consolidé"
            value={fxofExact(marginConsolidated)}
            sub={`${marginConsolidatedPct}% du CA consolidé`}
            icon={TrendingUp}
            color={marginColor(marginConsolidatedPct)}
          />
        )}
      </div>
      {/* Ligne 3 : Coûts transporteurs + CA moyen + autres */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPICard label="Coûts Transporteurs" value={fxofExact(kpis.carrierCost)} sub={`${fmt(data.topCarriers?.length || 0)} transporteurs`} icon={Truck} color="blue" />
        <KPICard label="CA Moyen/Opération" value={fxofExact(kpis.avgRevenuePerOp)} sub="spot uniquement" icon={Target} color="violet" />
        <KPICard label="Taux de Recouvrement" value={`${kpis.collectionRate}%`} sub="opérations payées" icon={ShieldCheck} color={kpis.collectionRate >= 90 ? 'emerald' : 'red'} />
        <KPICard label="DSO Moyen" value={`${kpis.dso} jours`} sub="délai de paiement" icon={Clock} color={kpis.dso <= 30 ? 'emerald' : 'orange'} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPICard label="Concentration Top 1" value={`${concentration.top1Percent}%`} sub="du CA total" icon={AlertTriangle} color={concentration.top1Percent > 50 ? 'red' : 'emerald'} alert={concentration.top1Percent > 50} />
        <KPICard label="Coût/Tonne Moyen" value={fxofExact(data.costPerTonne?.average || 0)} sub={`${data.totalOperations} ops`} icon={Scale} color="blue" />
      </div>

      {/* Evolution Chart */}
      {evolution.length > 0 && (
        <Card className="border border-gray-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-600" />Évolution CA / Coûts / Marge
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64" data-testid="evolution-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={evolution} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={fmt} width={50} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="revenue" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.15} name="CA" />
                  <Area type="monotone" dataKey="carrierCost" stackId="2" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.1} name="Coûts Transport" />
                  <Area type="monotone" dataKey="margin" stackId="3" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.15} name="Marge" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment Methods + Concentration */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border border-gray-100">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-emerald-600" />Modes de paiement
            </h3>
            {paymentMethods?.length > 0 ? (
              <div className="space-y-2">
                {paymentMethods.map((pm, i) => (
                  <div key={pm.method} className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 w-24 truncate">{pm.method}</span>
                    <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pm.percent}%`, backgroundColor: COLORS[i] }} />
                    </div>
                    <span className="text-xs font-mono font-semibold text-gray-800 w-16 text-right">{pm.percent}%</span>
                    <span className="text-xs text-gray-400 w-20 text-right">{fmt(pm.amount)}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-gray-400 text-center py-4">Pas de données</p>}
          </CardContent>
        </Card>

        <Card className={`border ${concentration.top1Percent > 50 ? 'border-red-200' : 'border-gray-100'}`}>
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <AlertTriangle className={`h-4 w-4 ${concentration.top1Percent > 50 ? 'text-red-500' : 'text-emerald-600'}`} />
              Concentration Client {concentration.top1Percent > 50 && <Badge variant="destructive" className="text-xs ml-1">Risque</Badge>}
            </h3>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-600">Top 1 client</span>
                  <span className="font-mono font-bold">{concentration.top1Percent}%</span>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${concentration.top1Percent > 50 ? 'bg-red-400' : 'bg-emerald-400'}`}
                    style={{ width: `${concentration.top1Percent}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-600">Top 3 clients</span>
                  <span className="font-mono font-bold">{concentration.top3Percent}%</span>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${concentration.top3Percent > 80 ? 'bg-orange-400' : 'bg-emerald-400'}`}
                    style={{ width: `${concentration.top3Percent}%` }} />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Indice HHI: <span className="font-mono font-semibold">{Math.round(concentration.hhi)}</span>
                <span className="ml-1">
                  {concentration.hhi > 2500 ? '(marché très concentré)' : concentration.hhi > 1500 ? '(modérément concentré)' : '(diversifié)'}
                </span>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ─── TAB: Carriers ───
const CarriersTab = ({ data }) => {
  const carriers = data.topCarriers || [];
  const maxPaid = Math.max(...carriers.map(c => c.totalPaid), 1);
  return (
    <div className="space-y-4">
      <Card className="border border-gray-100">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Top 10 Transporteurs par Montant Versé</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80" data-testid="carriers-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={carriers} layout="vertical" margin={{ left: 100, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tickFormatter={fmt} tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="totalPaid" fill="#3b82f6" name="Montant Versé" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-gray-100">
        <CardContent className="p-4">
          <table className="w-full text-xs" data-testid="carriers-table">
            <thead>
              <tr className="border-b text-gray-500 uppercase tracking-wider">
                <th className="text-left py-2 font-medium">#</th>
                <th className="text-left py-2 font-medium">Transporteur</th>
                <th className="text-right py-2 font-medium">Montant Versé</th>
                <th className="text-right py-2 font-medium">Nb Ops</th>
                <th className="text-right py-2 font-medium">Moy/Op</th>
                <th className="text-left py-2 font-medium pl-4">Part</th>
              </tr>
            </thead>
            <tbody>
              {carriers.map((c, i) => {
                const pct = data.kpis?.carrierCost > 0 ? (c.totalPaid / data.kpis.carrierCost * 100).toFixed(1) : 0;
                return (
                  <tr key={c.name} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-2 font-mono text-gray-400">{i + 1}</td>
                    <td className="py-2 font-medium text-gray-900">{c.name}</td>
                    <td className="py-2 text-right font-mono font-semibold">{fmtExact(c.totalPaid)}</td>
                    <td className="py-2 text-right font-mono text-gray-600">{c.opsCount}</td>
                    <td className="py-2 text-right font-mono text-gray-600">{fmtExact(c.avgPerOp)}</td>
                    <td className="py-2 pl-4">
                      <div className="flex items-center gap-1">
                        <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-gray-500 font-mono">{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

// ─── TAB: Routes ───
const RoutesTab = ({ data }) => {
  const routes = data.topRoutes || [];
  return (
    <Card className="border border-gray-100">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold">Top 15 Trajets par CA</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="routes-table">
            <thead>
              <tr className="border-b text-gray-500 uppercase tracking-wider bg-gray-50/50">
                <th className="text-left py-2.5 px-4 font-medium">#</th>
                <th className="text-left py-2.5 px-3 font-medium">Trajet</th>
                <th className="text-right py-2.5 px-3 font-medium">Nb Ops</th>
                <th className="text-right py-2.5 px-3 font-medium">CA Client</th>
                <th className="text-right py-2.5 px-3 font-medium">Coût Transport</th>
                <th className="text-right py-2.5 px-3 font-medium">Marge</th>
                <th className="text-right py-2.5 px-4 font-medium">Marge %</th>
              </tr>
            </thead>
            <tbody>
              {routes.map((r, i) => (
                <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="py-2.5 px-4 font-mono text-gray-400">{i + 1}</td>
                  <td className="py-2.5 px-3">
                    <span className="text-gray-900 font-medium">{r.departure}</span>
                    <ArrowRight className="inline h-3 w-3 mx-1 text-gray-400" />
                    <span className="text-gray-700">{r.arrival}</span>
                  </td>
                  <td className="py-2.5 px-3 text-right font-mono">{r.count}</td>
                  <td className="py-2.5 px-3 text-right font-mono font-semibold text-emerald-700">{fmtExact(r.revenue)}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-blue-700">{fmtExact(r.carrierCost)}</td>
                  <td className="py-2.5 px-3 text-right font-mono font-semibold">{fmtExact(r.margin)}</td>
                  <td className="py-2.5 px-4 text-right">
                    <Badge variant={r.marginPercent >= 25 ? 'default' : r.marginPercent >= 15 ? 'secondary' : 'destructive'}
                      className={`text-xs font-mono ${r.marginPercent >= 25 ? 'bg-emerald-100 text-emerald-700' : r.marginPercent >= 15 ? 'bg-orange-100 text-orange-700' : ''}`}>
                      {r.marginPercent}%
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── TAB: Profitability ───
const ProfitabilityTab = ({ data }) => {
  const clients = data.profitByClient || [];
  const totalRev = data.kpis?.revenue || 1;
  return (
    <div className="space-y-4">
      <Card className="border border-gray-100">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Rentabilité par Client</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="profitability-table">
              <thead>
                <tr className="border-b text-gray-500 uppercase tracking-wider bg-gray-50/50">
                  <th className="text-left py-2.5 px-4 font-medium">#</th>
                  <th className="text-left py-2.5 px-3 font-medium">Client</th>
                  <th className="text-right py-2.5 px-3 font-medium">CA</th>
                  <th className="text-right py-2.5 px-3 font-medium">Coûts</th>
                  <th className="text-right py-2.5 px-3 font-medium">Marge</th>
                  <th className="text-right py-2.5 px-3 font-medium">Marge %</th>
                  <th className="text-right py-2.5 px-3 font-medium">Nb Ops</th>
                  <th className="text-left py-2.5 px-4 font-medium">Part CA</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c, i) => {
                  const share = (c.revenue / totalRev * 100).toFixed(1);
                  return (
                    <tr key={c.name} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-2.5 px-4 font-mono text-gray-400">{i + 1}</td>
                      <td className="py-2.5 px-3 font-medium text-gray-900">{c.name}</td>
                      <td className="py-2.5 px-3 text-right font-mono font-semibold text-emerald-700">{fmtExact(c.revenue)}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-blue-700">{fmtExact(c.carrierCost)}</td>
                      <td className="py-2.5 px-3 text-right font-mono font-semibold">{fmtExact(c.margin)}</td>
                      <td className="py-2.5 px-3 text-right">
                        <Badge className={`text-xs font-mono ${c.marginPercent >= 25 ? 'bg-emerald-100 text-emerald-700' : c.marginPercent >= 15 ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'}`}>
                          {c.marginPercent}%
                        </Badge>
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono">{c.count}</td>
                      <td className="py-2.5 px-4">
                        <div className="flex items-center gap-1">
                          <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${share}%` }} />
                          </div>
                          <span className="text-gray-500 font-mono">{share}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ─── TAB: Cost per Tonne ───
const CostTonneTab = ({ data }) => {
  const items = data.costPerTonne?.byVehicle || [];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <KPICard label="Coût Moyen / Tonne" value={fxofExact(data.costPerTonne?.average || 0)} icon={Scale} color="blue" />
      </div>
      <Card className="border border-gray-100">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Coût par Tonne — par Type de Véhicule</CardTitle>
        </CardHeader>
        <CardContent>
          {items.length > 0 ? (
            <>
              <div className="h-60 mb-4" data-testid="cost-tonne-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={items} margin={{ left: 80, right: 20 }} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tickFormatter={fmt} tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="label" tick={{ fontSize: 10 }} width={80} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="avgCostPerTonne" fill="#8b5cf6" name="Coût/Tonne" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <table className="w-full text-xs">
                <thead><tr className="border-b text-gray-500 uppercase">
                  <th className="text-left py-2 font-medium">Véhicule</th>
                  <th className="text-right py-2 font-medium">Coût/Tonne</th>
                  <th className="text-right py-2 font-medium">Coût Total</th>
                  <th className="text-right py-2 font-medium">Tonnage</th>
                  <th className="text-right py-2 font-medium">Nb Ops</th>
                </tr></thead>
                <tbody>{items.map((it, i) => (
                  <tr key={it.type} className="border-b border-gray-50">
                    <td className="py-2 font-medium">{it.label}</td>
                    <td className="py-2 text-right font-mono font-semibold text-violet-700">{fmtExact(it.avgCostPerTonne)}</td>
                    <td className="py-2 text-right font-mono">{fmtExact(it.totalCost)}</td>
                    <td className="py-2 text-right font-mono">{it.totalWeight} T</td>
                    <td className="py-2 text-right font-mono">{it.count}</td>
                  </tr>
                ))}</tbody>
              </table>
            </>
          ) : <p className="text-sm text-gray-400 text-center py-8">Pas de données de tonnage</p>}
        </CardContent>
      </Card>
    </div>
  );
};

// ─── TAB: Cost per Vehicle ───
const CostVehicleTab = ({ data }) => {
  const items = data.costPerVehicle || [];
  return (
    <Card className="border border-gray-100">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold">Analyse des Coûts par Type de Véhicule</CardTitle>
      </CardHeader>
      <CardContent>
        {items.length > 0 ? (
          <>
            <div className="h-60 mb-4" data-testid="cost-vehicle-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={items} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tickFormatter={fmt} tick={{ fontSize: 10 }} width={50} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="totalRevenue" fill="#10b981" name="CA" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="totalCost" fill="#3b82f6" name="Coûts" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="margin" fill="#f59e0b" name="Marge" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <table className="w-full text-xs">
              <thead><tr className="border-b text-gray-500 uppercase">
                <th className="text-left py-2 font-medium">Véhicule</th>
                <th className="text-right py-2 font-medium">Nb Ops</th>
                <th className="text-right py-2 font-medium">CA Total</th>
                <th className="text-right py-2 font-medium">Coût Total</th>
                <th className="text-right py-2 font-medium">Marge</th>
                <th className="text-right py-2 font-medium">Marge %</th>
                <th className="text-right py-2 font-medium">Coût Moy/Op</th>
              </tr></thead>
              <tbody>{items.map((it) => (
                <tr key={it.type} className="border-b border-gray-50">
                  <td className="py-2 font-medium">{it.label}</td>
                  <td className="py-2 text-right font-mono">{it.count}</td>
                  <td className="py-2 text-right font-mono text-emerald-700">{fmtExact(it.totalRevenue)}</td>
                  <td className="py-2 text-right font-mono text-blue-700">{fmtExact(it.totalCost)}</td>
                  <td className="py-2 text-right font-mono font-semibold">{fmtExact(it.margin)}</td>
                  <td className="py-2 text-right">
                    <Badge className={`text-xs font-mono ${it.marginPercent >= 25 ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'}`}>
                      {it.marginPercent}%
                    </Badge>
                  </td>
                  <td className="py-2 text-right font-mono">{fmtExact(it.avgCost)}</td>
                </tr>
              ))}</tbody>
            </table>
          </>
        ) : <p className="text-sm text-gray-400 text-center py-8">Pas de données</p>}
      </CardContent>
    </Card>
  );
};

// ─── TAB: Cost per Destination ───
const CostDestTab = ({ data }) => {
  const items = data.costPerDestination || [];
  return (
    <Card className="border border-gray-100">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold">Analyse des Coûts par Destination</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="cost-dest-table">
            <thead>
              <tr className="border-b text-gray-500 uppercase tracking-wider bg-gray-50/50">
                <th className="text-left py-2.5 px-4 font-medium">#</th>
                <th className="text-left py-2.5 px-3 font-medium">Destination</th>
                <th className="text-right py-2.5 px-3 font-medium">Nb Ops</th>
                <th className="text-right py-2.5 px-3 font-medium">CA Total</th>
                <th className="text-right py-2.5 px-3 font-medium">Coût Total</th>
                <th className="text-right py-2.5 px-3 font-medium">Marge</th>
                <th className="text-right py-2.5 px-3 font-medium">Marge %</th>
                <th className="text-right py-2.5 px-4 font-medium">Coût Moy/Op</th>
              </tr>
            </thead>
            <tbody>
              {items.map((d, i) => (
                <tr key={d.city} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="py-2.5 px-4 font-mono text-gray-400">{i + 1}</td>
                  <td className="py-2.5 px-3 font-medium text-gray-900">{d.city}</td>
                  <td className="py-2.5 px-3 text-right font-mono">{d.count}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-emerald-700">{fmtExact(d.totalRevenue)}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-blue-700">{fmtExact(d.totalCost)}</td>
                  <td className="py-2.5 px-3 text-right font-mono font-semibold">{fmtExact(d.margin)}</td>
                  <td className="py-2.5 px-3 text-right">
                    <Badge className={`text-xs font-mono ${d.marginPercent >= 25 ? 'bg-emerald-100 text-emerald-700' : d.marginPercent >= 15 ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'}`}>
                      {d.marginPercent}%
                    </Badge>
                  </td>
                  <td className="py-2.5 px-4 text-right font-mono">{fmtExact(d.avgCost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// Map parent dashboard period keys to financial dashboard keys
const PARENT_PERIOD_MAP = {
  '7d': '7d', '30d': '1m', '90d': '3m', '12m': '1y', 'ytd': '1y', 'all': 'all'
};

// ─── MAIN COMPONENT ───
const FinancialKPIs = ({ period: externalPeriod, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [activePeriod, setActivePeriod] = useState(() => PARENT_PERIOD_MAP[externalPeriod] || 'all');

  // Sync with parent period when it changes
  useEffect(() => {
    const mapped = PARENT_PERIOD_MAP[externalPeriod] || externalPeriod || 'all';
    setActivePeriod(mapped);
  }, [externalPeriod]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/dashboard/financial-dashboard`, {
        params: { period: activePeriod }});
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [activePeriod, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
    </div>
  );

  if (error) return (
    <Card className="border-red-200 bg-red-50">
      <CardContent className="p-6 flex items-center gap-2 text-red-600">
        <AlertTriangle className="h-5 w-5" /><span>{error}</span>
        <button onClick={fetchData} className="ml-auto text-sm underline">Réessayer</button>
      </CardContent>
    </Card>
  );

  if (!data) return null;

  const TabContent = {
    overview: OverviewTab,
    carriers: CarriersTab,
    routes: RoutesTab,
    profitability: ProfitabilityTab,
    costTonne: CostTonneTab,
    costVehicle: CostVehicleTab,
    costDest: CostDestTab,
  }[activeTab] || OverviewTab;

  return (
    <div className="space-y-4" data-testid="financial-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-emerald-600" />Tableau de Bord Financier
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Analyse des coûts et performances financières — {data.totalOperations} opérations
          </p>
        </div>
        <button onClick={fetchData} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="refresh-financial-btn">
          <RefreshCw className="h-4 w-4 text-gray-500" />
        </button>
      </div>

      {/* Period Selector */}
      <div className="flex items-center gap-1 flex-wrap" data-testid="period-selector">
        {PERIODS.map(p => (
          <button key={p.key} onClick={() => setActivePeriod(p.key)}
            data-testid={`period-${p.key}`}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              activePeriod === p.key
                ? 'bg-emerald-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1 border-b border-gray-200" data-testid="financial-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium whitespace-nowrap rounded-t-md transition-colors ${
                activeTab === t.key
                  ? 'text-emerald-700 border-b-2 border-emerald-600 bg-emerald-50/50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}>
              <Icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <TabContent data={data} />
    </div>
  );
};

export default FinancialKPIs;
